// src/pages/usermanagement/UserDetail.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { 
  ArrowLeft, 
  Edit2, 
  Mail, 
  Phone, 
  Shield, 
  Calendar, 
  Clock,
  User as UserIcon,
  CheckCircle,
  XCircle
} from 'lucide-react';

// Services
import userService from '../../services/userService';

// Auth
import { HasPermission } from '../../components/auth';
import { PERMISSIONS, ROLE_LABELS, STATUS_LABELS } from '../../components/auth';

const UserDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadUser();
  }, [id]);

  const loadUser = async () => {
    try {
      setLoading(true);
      const data = await userService.getUserById(id);
      setUser(data);
    } catch (error) {
      console.error('Failed to load user:', error);
      toast.error('Failed to load user details');
      navigate('/usermanagement');
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return '-';
    const date = new Date(dateTimeString);
    return date.toLocaleDateString('id-ID', { 
      day: '2-digit', 
      month: 'long', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="bg-white rounded-lg shadow-md p-8 text-center">
        <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">User Not Found</h2>
        <p className="text-gray-600 mb-4">The user you're looking for doesn't exist.</p>
        <button
          onClick={() => navigate('/usermanagement')}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Back to User List
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/usermanagement')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Back to List"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">User Details</h1>
              <p className="text-sm text-gray-600 mt-1">View user information and settings</p>
            </div>
          </div>

          <HasPermission permission={PERMISSIONS.USER_UPDATE}>
            <button
              onClick={() => navigate(`/usermanagement/${id}/edit`)}
              className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-md hover:shadow-lg"
            >
              <Edit2 className="w-4 h-4" />
              Edit User
            </button>
          </HasPermission>
        </div>
      </div>

      {/* User Profile Card */}
      <div className="bg-white rounded-lg shadow-md p-8 border border-gray-200">
        <div className="flex items-start gap-8">
          {/* Avatar */}
          <div className="flex-shrink-0">
            <div className="w-32 h-32 bg-gradient-to-br from-blue-600 to-purple-600 rounded-full flex items-center justify-center text-white text-4xl font-bold shadow-lg">
              {user.username?.charAt(0).toUpperCase() || 'U'}
            </div>
            <div className="mt-4 text-center">
              <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold ${
                user.status === 'ACTIVE' 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-gray-100 text-gray-800'
              }`}>
                {user.status === 'ACTIVE' ? (
                  <CheckCircle className="w-4 h-4" />
                ) : (
                  <XCircle className="w-4 h-4" />
                )}
                {STATUS_LABELS[user.status] || user.status}
              </span>
            </div>
          </div>

          {/* User Information */}
          <div className="flex-1">
            <div className="grid grid-cols-2 gap-6">
              {/* Username */}
              <div>
                <label className="text-sm font-semibold text-gray-600 flex items-center gap-2">
                  <UserIcon className="w-4 h-4" />
                  Username
                </label>
                <p className="text-xl font-bold text-red-600 mt-2">{user.username}</p>
              </div>

              {/* Full Name */}
              <div>
                <label className="text-sm font-semibold text-gray-600">Full Name</label>
                <p className="text-lg font-semibold text-gray-900 mt-2">{user.fullName}</p>
              </div>

              {/* Email */}
              <div>
                <label className="text-sm font-semibold text-gray-600 flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Email Address
                </label>
                <p className="text-gray-900 mt-2">{user.email}</p>
              </div>

              {/* Phone */}
              <div>
                <label className="text-sm font-semibold text-gray-600 flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  Phone Number
                </label>
                <p className="text-gray-900 mt-2">{user.phone || '-'}</p>
              </div>

              {/* Role */}
              <div>
                <label className="text-sm font-semibold text-gray-600 flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  Role
                </label>
                <div className="mt-2">
                  <span className="inline-flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-800 rounded-full text-sm font-semibold">
                    <Shield className="w-4 h-4" />
                    {ROLE_LABELS[user.role] || user.role}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Additional Information Grid */}
      <div className="grid grid-cols-2 gap-6">
        {/* Account Information */}
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-600" />
            Account Information
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-gray-100">
              <span className="text-sm font-medium text-gray-600">Created At</span>
              <span className="text-sm text-gray-900">{formatDateTime(user.createdAt)}</span>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-gray-100">
              <span className="text-sm font-medium text-gray-600">Last Updated</span>
              <span className="text-sm text-gray-900">{formatDateTime(user.updatedAt)}</span>
            </div>
            <div className="flex items-center justify-between py-3">
              <span className="text-sm font-medium text-gray-600 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Last Login
              </span>
              <span className="text-sm text-gray-900">
                {formatDateTime(user.lastLogin)}
              </span>
            </div>
          </div>
        </div>

        {/* Permissions */}
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5 text-purple-600" />
            Permissions ({user.permissions?.length || 0})
          </h3>
          <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto">
            {user.permissions && user.permissions.length > 0 ? (
              user.permissions.map(perm => (
                <span 
                  key={perm} 
                  className="px-3 py-1 bg-blue-100 text-blue-700 rounded-lg text-xs font-medium"
                >
                  {perm}
                </span>
              ))
            ) : (
              <p className="text-sm text-gray-500 italic">No permissions assigned</p>
            )}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between items-center bg-white rounded-lg shadow-md p-6 border border-gray-200">
        <button
          onClick={() => navigate('/usermanagement')}
          className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
        >
          Back to List
        </button>

        <HasPermission permission={PERMISSIONS.USER_UPDATE}>
          <button
            onClick={() => navigate(`/usermanagement/${id}/edit`)}
            className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Edit2 className="w-4 h-4" />
            Edit User
          </button>
        </HasPermission>
      </div>
    </div>
  );
};

export default UserDetail;